import { useState } from "react";
import { format, addDays, subDays, getISOWeek } from "date-fns";
import { sv } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ChevronLeft, ChevronRight, Search, Plus, Ban, Printer, Unlock, CalendarIcon, MousePointerClick } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { ScheduleBooking, SelectionInfo } from "./types";

interface ScheduleGridToolbarProps {
  selectedDate: Date;
  setSelectedDate: (d: Date) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  clearSelection: () => void;
  onCreateBooking: () => void;
  selectionInfo: SelectionInfo | null;
  onBlockTime: () => void;
  onUnblockTime: () => void;
  blockedCount: number;
  isBooking: boolean;
  filteredBookings: ScheduleBooking[] | null;
  getPlayerName: (b: ScheduleBooking) => string;
  onBookingClick: (b: ScheduleBooking) => void;
  onBookFromSelection: () => void;
  // PR-AK — Multi-select mode toggle.
  multiSelectMode: boolean;
  onToggleMultiSelect: () => void;
  selectedBookingCount: number;
}

export function ScheduleGridToolbar({
  selectedDate,
  setSelectedDate,
  searchQuery,
  setSearchQuery,
  clearSelection,
  onCreateBooking,
  selectionInfo,
  onBlockTime,
  onUnblockTime,
  blockedCount,
  isBooking,
  filteredBookings,
  getPlayerName,
  onBookingClick,
  onBookFromSelection,
  multiSelectMode,
  onToggleMultiSelect,
  selectedBookingCount,
}: ScheduleGridToolbarProps) {
  const { t } = useTranslation("admin");
  const { t: tBooking } = useTranslation("booking");
  const weekNumber = getISOWeek(selectedDate);
  const [calendarOpen, setCalendarOpen] = useState(false);

  return (
    <>
      {/* Header */}
      <div className="flex flex-col gap-2 mb-3">
        {/* Date navigation — always first on mobile */}
        <div className="flex items-center justify-center sm:justify-end gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => {
              setSelectedDate(subDays(selectedDate, 1));
              clearSelection();
            }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <button className="text-center min-w-[180px] sm:min-w-[200px] hover:bg-muted/50 rounded-md px-2 py-1 transition-colors cursor-pointer">
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase flex items-center justify-center gap-1">
                  <CalendarIcon className="h-3 w-3" />
                  {t("scheduleToolbar.week")} {weekNumber}
                </p>
                <p className="font-semibold text-xs sm:text-sm">
                  {format(selectedDate, "EEEE d MMMM", { locale: sv })}
                </p>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  if (date) {
                    setSelectedDate(date);
                    clearSelection();
                  }
                  setCalendarOpen(false);
                }}
                locale={sv}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => {
              setSelectedDate(addDays(selectedDate, 1));
              clearSelection();
            }}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => {
              setSelectedDate(new Date());
              clearSelection();
            }}
          >
            {t("scheduleToolbar.today")}
          </Button>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <Button size="sm" className="h-9 flex-1 sm:flex-none" onClick={onCreateBooking}>
            <Plus className="h-4 w-4 mr-1" />
            {t("scheduleToolbar.createBooking")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-9 flex-1 sm:flex-none text-orange-600 border-orange-300 hover:bg-orange-50"
            onClick={() =>
              toast.info(t("scheduleToolbar.blockTimeHint"))
            }
          >
            <Ban className="h-4 w-4 mr-1" />
            {t("scheduleToolbar.blockTime")}
          </Button>
          {/* PR-AK — Multi-select mode toggle for bulk-cancel */}
          <Button
            size="sm"
            variant={multiSelectMode ? "default" : "outline"}
            className={cn(
              "h-9 flex-1 sm:flex-none",
              multiSelectMode && "bg-primary text-primary-foreground hover:bg-primary/90",
            )}
            onClick={onToggleMultiSelect}
            title={t("scheduleToolbar.multiSelectHint", { defaultValue: "Markera flera bokningar för bulk-avbokning" })}
          >
            <MousePointerClick className="h-4 w-4 mr-1" />
            {multiSelectMode
              ? t("scheduleToolbar.multiSelectActive", { defaultValue: "Markeringsläge ({{count}})", count: selectedBookingCount })
              : t("scheduleToolbar.multiSelectStart", { defaultValue: "Markera flera" })}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-9 hidden sm:inline-flex"
            onClick={() => window.print()}
          >
            <Printer className="h-4 w-4 mr-1" />
            {t("scheduleToolbar.print")}
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("scheduleToolbar.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 w-full sm:w-60"
          />
        </div>
      </div>

      {/* Search results */}
      {filteredBookings && (
        <div className="bg-muted/50 rounded-lg p-3 space-y-2 mb-3">
          <p className="text-xs font-medium text-muted-foreground">
            {t("scheduleToolbar.searchResults")} ({filteredBookings.length})
          </p>
          {filteredBookings.map((b) => (
            <div
              key={b.id}
              className="flex justify-between items-center text-sm bg-background rounded p-2 cursor-pointer hover:bg-accent"
              onClick={() => onBookingClick(b)}
            >
              <span>{getPlayerName(b)}</span>
              <span className="text-muted-foreground">
                {format(new Date(b.start_time), "HH:mm")}–
                {format(new Date(b.end_time), "HH:mm")} · {b.courts?.name}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Selection summary bar */}
      {selectionInfo && (
        <div className="flex items-center justify-between bg-primary/10 border border-primary/20 rounded-lg px-4 py-2 mb-3">
          <div className="text-sm">
            <span className="font-medium">{selectionInfo.courtLabel}</span>
            {" · "}
            {format(selectionInfo.startTime, "HH:mm")}–
            {format(selectionInfo.endTime, "HH:mm")}
            {" · "}
            {selectionInfo.durationMinutes} min
            {selectionInfo.courtCount > 1 && (
              <span className="ml-1 text-muted-foreground">
                · {selectionInfo.courtCount} banor
              </span>
            )}
            {selectionInfo.price && selectionInfo.price.perHour !== null && (
              <span className="font-semibold ml-2">
                {selectionInfo.price.total} kr
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={clearSelection}>
              {t("scheduleToolbar.clear")}
            </Button>
            {blockedCount > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="text-green-700 border-green-400 hover:bg-green-50"
                onClick={onUnblockTime}
                disabled={isBooking}
              >
                <Unlock className="h-4 w-4 mr-1" />
                {blockedCount === 1
                  ? tBooking("unblock")
                  : tBooking("unblockMultiple", { count: blockedCount })}
              </Button>
            )}
            {/* Show block button when there are unblocked (empty) slots in selection */}
            <Button
              size="sm"
              variant="outline"
              className="text-destructive border-destructive/50 hover:bg-destructive/10"
              onClick={onBlockTime}
              disabled={isBooking}
            >
              <Ban className="h-4 w-4 mr-1" />
              {t("scheduleToolbar.block")}
            </Button>
            <Button size="sm" onClick={onBookFromSelection}>
              {t("scheduleToolbar.book")}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
